import type { LoadTimeTrackData, TrackEvent } from "common-types";
import type { BeaconTracksMappingParams, LoadTimeSenderBody } from "../types";
import { send } from "./sender";
import {
  getArticleId,
  getBeaconId,
  getPageLang,
  getSessionId,
  getTrackingPv,
  getTrackingPvGlobal,
  getTrackingPvUid,
  getUserId,
  getWikiId,
  getWikiName,
  getWikiSkin,
} from "../utils/beaconParams";
import { getExpBucket } from "utils";

export async function sendLoadTimes(
  { event, payload }: LoadTimeTrackData,
  options?: BeaconTracksMappingParams,
): Promise<void> {
  const nowDate = new Date();
  const geo = window.fandomHost!.geo!;
  const body: LoadTimeSenderBody = {
    event_name: buildEventName(event, payload, options),
    browser_ts: nowDate.getTime(),
    load_time: Math.round(payload.time),
    tz_offset: nowDate.getTimezoneOffset(),
    country: geo.country,
    session_id: getSessionId(),
    pv_number: getTrackingPv(),
    pv_number_global: getTrackingPvGlobal(),
    pv_unique_id: getTrackingPvUid(),
    beacon: getBeaconId(),
    c: getWikiId(),
    x: getWikiName(),
    lc: getPageLang(),
    s: getWikiSkin(),
    ua: window.navigator.userAgent,
    u: getUserId(),
    a: getArticleId(),
    n: -1,
    exp_bucket: getExpBucket(),
    odyssey_version: window.fandomAds?.version ?? "unknown",
  };

  send("adengloadtimes", body);
}

/**
 * Creates the `event_name` property for the provided event. It will build the name using the
 * `payload.slotName`, `payload.eventName`, `options.dwhEventName`, or the value of `event`, using
 * the following rules:
 * 1. `payload.slotName` is defined, use that as a prefix of `${slotName}_`
 * 2. Use of the following as the rest of the event name (format: `${step1 ?? ""}${step2}`)
 *   - If `options.dwhEventName` is defined use that
 *   - Then if `payload.EventName` is defined, use that
 *   - Last if neither is set, use the `event` enum string name.
 *
 * Examples:
 *   - `buildEventName(Tracks.AE_INIT, {dwhEventName: "load_time_init"}, {})` -> "load_time_init"
 *   - `buildEventName(Tracks.VENDOR_TIME_UPDATE, {}, {eventName: "audigent_segments_ready"})` -> "audigent_segments_ready"
 *   - `buildEventName(Tracks.TEST, {}, {})` -> "test"
 *
 * @param event The event being tracked
 * @param payload The event payload with the slotName or eventName properties possibly defined
 * @param options Tracking options with possible dwhEventName override
 * @returns
 */
function buildEventName(
  event: TrackEvent,
  payload: LoadTimeTrackData["payload"],
  options?: BeaconTracksMappingParams,
): string {
  const prefix = payload.slotName ? `${payload.slotName}_` : "";

  return `${prefix}${options?.beaconEventName ?? payload.eventName ?? event}`;
}
